import EventEmitter from "eventemitter3";
import DataStoreProps from "../types/DataStore";
import StoreProps from "../types/Store";
import CookieHandler from "./CookieHandler";
import GeoLocation from "./GeoLocation";
import WindowSize from "./WindowSize";

declare let dataStore: DataStoreProps;

interface StoreDistance {
    store: StoreProps;
    distance: number;
}

class Store {
    EEE: EventEmitter;
    current: StoreProps | null;
    loading: boolean;
    single_id: number;
    stores: StoreProps[];
    isMobile: boolean;
    currentStoreCookieName: string;
    cookieLife: number;
    CookieHandler: CookieHandler;
    GeoLocation: GeoLocation;

    constructor() {
        this.EEE = new EventEmitter();
        this.current = null;
        this.loading = false;
        this.single_id = parseInt(dataStore.single_id);
        this.stores = dataStore.stores;
        this.isMobile = WindowSize.isMobile();
        this.currentStoreCookieName = "currentStore";
        this.cookieLife = 2;
        this.CookieHandler = new CookieHandler();
        this.GeoLocation = new GeoLocation();
    }

    /**
     * Get all stores
     * @returns {Array|*}
     */
    getAllStores() {
        return this.stores;
    }

    /**
     * Get current store
     * @param callback
     * @returns {*}
     */
    getCurrentStore() {
        let returnDefault = true;

        /**
         * If set return that
         */
        if (this.current) {
            return this.current;
        }

        /**
         * If single store post
         */
        if (this.single_id) {
            const currentStore = this.getStoreByID(this.single_id);
            returnDefault = false;
            this.current = currentStore;
            return this.current;
        }

        /**
         * If cookie is set get that store
         */
        if (this.CookieHandler.checkCookie(this.currentStoreCookieName)) {
            const currentStoreID = this.CookieHandler.getCookie(
                this.currentStoreCookieName,
            );
            const currentStore = this.getStoreByID(parseInt(currentStoreID));
            if (!currentStore) {
                this.CookieHandler.deleteCookie(this.currentStoreCookieName);
            } else {
                returnDefault = false;
                this.current = currentStore;
                return this.current;
            }
        }

        /**
         * If Mobile get position
         * & set that store in cookie
         */
        if (this.isMobile) {
            this.loading = true;
            this.EEE.emit("STORE_LOADING", this.loading);
            this.getStoreByPosition((store: StoreProps | null) => {
                this.loading = false;
                this.EEE.emit("STORE_LOADING", this.loading);

                if (store) {
                    this.EEE.emit("STORE_CHANGED", store.id);
                    this.setStore(store.id);
                    this.current = store;
                    return this.current;
                }
            });
        }

        /**
         * If no other options
         * return first store
         */
        if (returnDefault) {
            this.current = this.current ? this.current : this.stores[0];
            return this.current;
        }
    }

    /**
     * Get closest store by position
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getStoreByPosition(callback: any) {
        if (this.isMobile) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.GeoLocation.getCurrentPosition((response: any): any => {
                if (response !== "error") {
                    return callback(this.getClosestStore(response));
                } else {
                    return callback(this.getCurrentStore());
                }
            });
        } else {
            return callback(this.getCurrentStore());
        }
    }

    /**
     * Get closest store
     * @param position
     * @returns {*}
     */
    getClosestStore(position: { lat: string; lng: string }) {
        const distances: StoreDistance[] = [];

        [...this.stores].forEach((store) => {
            if (store.lat !== "" && store.lng !== "") {
                distances.push({
                    store,
                    distance: this.GeoLocation.getDistance(
                        parseFloat(store.lat),
                        parseFloat(store.lng),
                        parseFloat(position.lat),
                        parseFloat(position.lng),
                        "K",
                    ),
                });
            }
        });
        if (distances.length > 0) {
            distances.sort((a, b) => {
                return a.distance - b.distance;
            });
            return distances[0].store;
        } else {
            return this.getCurrentStore();
        }
    }

    /**
     * Get store by id
     * @param id
     */
    getStoreByID(id: number): StoreProps | null {
        let returnStore = null;
        [...this.stores].forEach((store) => {
            if (store.id === id) {
                returnStore = store;
            }
        });
        return returnStore;
    }

    /**
     * Set store and cookie
     * @param id
     */
    setStore(id: number): void {
        this.current = this.getStoreByID(id);
        this.CookieHandler.setCookie(
            this.currentStoreCookieName,
            this.current ? this.current.id.toString() : "",
            this.cookieLife,
        );
    }
}

export default new Store();
